/**
 * The app's one XML pretty-printer (owner, 30.09.2026: request bodies and
 * responses were not pretty-printed — three separate regex formatters, all
 * line-based, broke on a start tag spread over several lines and lost the
 * indentation of everything inside it).
 *
 * Token-based, single pass, linear:
 *   <?…?>  <!-- … -->  <![CDATA[…]]>  <!DOCTYPE …>  start / end / self-closing
 *   tags (quote-aware — a `>` inside an attribute value does not end the tag)
 *   and text.
 *
 * Output rules
 *  - declaration, doctype, comments and every element on their own line,
 *    indented by depth;
 *  - a tag's attributes stay on its line, whitespace between them collapsed;
 *  - an element holding only text / CDATA stays on one line, the text exactly
 *    as it was (whitespace inside text is data);
 *  - an element with MIXED content (text next to elements) is copied verbatim;
 *  - malformed input → null (the caller keeps the original and may say so).
 *
 * formatXml(format(x)) === format(x).
 */

const NAME_START = /[A-Za-z_:À-￿]/;

function tagEnd(src, from) {
  // Index of the `>` closing the tag that starts at `from`, skipping quoted values.
  let quote = null;
  for (let i = from + 1; i < src.length; i += 1) {
    const c = src[i];
    if (quote) {
      if (c === quote) quote = null;
    } else if (c === '"' || c === "'") {
      quote = c;
    } else if (c === '>') {
      return i;
    } else if (c === '<') {
      return -1;
    }
  }
  return -1;
}

/** `<a   x="1"\n  y='2' >` → `<a x="1" y='2'>`; whitespace inside quotes untouched. */
function collapseTag(raw) {
  let out = '';
  let quote = null;
  let space = false;
  for (let i = 0; i < raw.length; i += 1) {
    const c = raw[i];
    if (quote) {
      out += c;
      if (c === quote) quote = null;
      continue;
    }
    if (c === '"' || c === "'") quote = c;
    if (/\s/.test(c)) {
      space = true;
      continue;
    }
    if (space) {
      if (c !== '>' && out.length && out[out.length - 1] !== '<') out += ' ';
      space = false;
    }
    out += c;
  }
  return out;
}

function tokenize(src) {
  const tokens = [];
  let i = 0;
  const n = src.length;
  while (i < n) {
    if (src[i] !== '<') {
      const next = src.indexOf('<', i);
      const end = next === -1 ? n : next;
      tokens.push({ type: 'text', raw: src.slice(i, end), start: i, end });
      i = end;
      continue;
    }
    const rest2 = src.slice(i, i + 9);
    let end;
    let type;
    if (rest2.startsWith('<?')) {
      end = src.indexOf('?>', i + 2);
      if (end === -1) return null;
      end += 2;
      type = 'pi';
    } else if (rest2.startsWith('<!--')) {
      end = src.indexOf('-->', i + 4);
      if (end === -1) return null;
      end += 3;
      type = 'comment';
    } else if (rest2.startsWith('<![CDATA[')) {
      end = src.indexOf(']]>', i + 9);
      if (end === -1) return null;
      end += 3;
      type = 'cdata';
    } else if (/^<!doctype/i.test(rest2)) {
      // A doctype may carry an internal subset in [ … ].
      const bracket = src.indexOf('[', i);
      const gt = src.indexOf('>', i);
      if (gt === -1) return null;
      if (bracket !== -1 && bracket < gt) {
        const close = src.indexOf(']', bracket);
        if (close === -1) return null;
        end = src.indexOf('>', close) + 1;
        if (end === 0) return null;
      } else {
        end = gt + 1;
      }
      type = 'doctype';
    } else if (src[i + 1] === '/') {
      const gt = src.indexOf('>', i);
      if (gt === -1) return null;
      end = gt + 1;
      const name = src.slice(i + 2, gt).trim();
      if (!name) return null;
      tokens.push({ type: 'close', name, raw: `</${name}>`, start: i, end });
      i = end;
      continue;
    } else if (NAME_START.test(src[i + 1] || '')) {
      const gt = tagEnd(src, i);
      if (gt === -1) return null;
      end = gt + 1;
      const raw = src.slice(i, end);
      const selfClosing = /\/\s*>$/.test(raw);
      const name = raw.slice(1).match(/^[^\s/>]+/)[0];
      tokens.push({ type: selfClosing ? 'empty' : 'open', name, raw: collapseTag(raw), start: i, end });
      i = end;
      continue;
    } else {
      return null; // a bare `<` — not XML
    }
    tokens.push({ type, raw: src.slice(i, end), start: i, end });
    i = end;
  }
  return tokens;
}

/** Tokens → tree; null when tags do not nest. */
function buildTree(tokens) {
  const root = { children: [] };
  const stack = [root];
  for (const t of tokens) {
    const parent = stack[stack.length - 1];
    if (t.type === 'open') {
      const el = { type: 'element', open: t, children: [] };
      parent.children.push(el);
      stack.push(el);
    } else if (t.type === 'close') {
      if (stack.length === 1 || parent.open.name !== t.name) return null;
      parent.close = t;
      stack.pop();
    } else {
      parent.children.push(t);
    }
  }
  return stack.length === 1 ? root : null;
}

const isBlank = (node) => node.type === 'text' && !node.raw.trim();

function serialize(node, depth, src, unit, out) {
  const pad = unit.repeat(depth);
  if (node.type !== 'element') {
    if (isBlank(node)) return;
    out.push(pad + node.raw); // a self-closing tag, comment, PI or doctype — as written
    return;
  }
  const kids = node.children;
  const open = node.open.raw;
  const close = node.close.raw;
  if (kids.length === 0) {
    out.push(`${pad}${open}${close}`);
    return;
  }
  const hasElement = kids.some((k) => k.type === 'element' || k.type === 'empty');
  const hasText = kids.some((k) => (k.type === 'text' && k.raw.trim()) || k.type === 'cdata');
  if (!hasElement && kids.every((k) => k.type === 'text' || k.type === 'cdata')) {
    // Text only: one line, text exactly as it was.
    out.push(`${pad}${open}${kids.map((k) => k.raw).join('')}${close}`);
    return;
  }
  if (hasElement && hasText) {
    // Mixed content: whitespace there is data — copy the element verbatim.
    out.push(pad + src.slice(node.open.start, node.close.end));
    return;
  }
  out.push(pad + open);
  for (const k of kids) serialize(k, depth + 1, src, unit, out);
  out.push(pad + close);
}

/**
 * Pretty-print XML. Returns the formatted text, or null when the input is
 * not well-formed enough to format safely (the caller keeps the original).
 */
export function formatXml(text, { indent = 2 } = {}) {
  const src = String(text ?? '').replace(/\r\n?/g, '\n');
  if (!src.trim()) return src;
  const tokens = tokenize(src);
  if (!tokens) return null;
  const tree = buildTree(tokens);
  if (!tree) return null;
  // Text at the top level (outside the root element) is not XML.
  if (tree.children.some((k) => k.type === 'text' && k.raw.trim())) return null;
  const out = [];
  const unit = ' '.repeat(indent);
  for (const k of tree.children) serialize(k, 0, src, unit, out);
  return out.join('\n');
}
